import { useState, useRef } from "react";
import "./BookmarkModal.css";
import { useFocusTrap, useRestoreFocus } from "../../hooks/useFocusTrap";

/**
 * Modal dialog for creating or editing a bookmark.
 * Pass `onAdd` for create mode, `onSave` for edit mode.
 * @param {{
 *   defaultName: string,
 *   defaultDescription?: string,
 *   contentId: string,
 *   onAdd?: (bookmark: { contentId: string, name: string, description: string }) => void,
 *   onSave?: (bookmark: { contentId: string, name: string, description: string }) => void,
 *   onClose: () => void,
 * }} props
 */
export default function BookmarkModal({
  defaultName,
  defaultDescription,
  contentId,
  scrollId,
  onAdd,
  onSave,
  onClose,
}) {
  useRestoreFocus();
  const containerRef = useRef(null);
  useFocusTrap(containerRef);

  const [name, setName] = useState(onSave ? (defaultName ?? "") : "");
  const [description, setDescription] = useState(defaultDescription ?? "");

  const isEditMode = Boolean(onSave);
  const canSubmit = name.trim().length >= 3;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const bookmark = {
      contentId,
      scrollId: scrollId ?? "",
      name: name.trim(),
      description: description.trim(),
    };
    if (isEditMode) {
      onSave(bookmark);
    } else {
      onAdd(bookmark);
    }
    onClose();
  };

  return (
    <div
      className="bm-modal__backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={isEditMode ? "Edit bookmark" : "Add bookmark"}
    >
      <div
        ref={containerRef}
        className="bm-modal__card"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="bm-modal__title">
          {isEditMode ? "Edit bookmark" : "Add bookmark"}
        </h2>
        <p className="bm-modal__desc">
          {isEditMode
            ? "Edit the name and description for this bookmark."
            : "Use this form to add a bookmark for the content you just highlighted. This allows you to return here quickly and is searchable from the home page."}
        </p>

        <form className="bm-modal__form" onSubmit={handleSubmit}>
          <label className="bm-modal__label" htmlFor="bm-name">
            Bookmark name
          </label>
          <input
            id="bm-name"
            className="bm-modal__input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Add bookmark name"
            required
            minLength={3}
            autoFocus
          />

          <label className="bm-modal__label" htmlFor="bm-description">
            Description
          </label>
          <textarea
            id="bm-description"
            className="bm-modal__textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="You can optionally enter a fuller description here which will be searchable from the home page"
            rows={4}
          />

          <div className="bm-modal__actions">
            <button
              type="button"
              className="bm-modal__cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bm-modal__submit"
              disabled={!canSubmit}
            >
              {isEditMode ? "Save changes" : "Add bookmark"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
